/* ==========================================================================
   Jackson Ultima - LEARN shared UI behaviours
   --------------------------------------------------------------------------
   Ported from the prototype's assets/js/site.js. Only the behaviours the learn
   sections need are carried over - the header, navigation, mobile drawer,
   footer and back-to-top are Impact's job and were dropped.

   Each behaviour is a custom element so the theme editor re-initialises it when
   a section reloads, which the original DOMContentLoaded wiring could not do.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Tabs - <js-learn-tabs> wraps a [role="tablist"] and its panels
   -------------------------------------------------------------------------- */
class JsLearnTabs extends HTMLElement {
  connectedCallback() {
    this.buildStageTabs();
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab, false));
      tab.addEventListener('keydown', (e) => {
        const i = this.tabs.indexOf(tab);
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = this.tabs[(i + 1) % this.tabs.length];
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
        if (e.key === 'Home') next = this.tabs[0];
        if (e.key === 'End') next = this.tabs[this.tabs.length - 1];
        if (next) {
          e.preventDefault();
          this.select(next, true);
        }
      });
    });

    const initial = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.select(initial, false);
  }

  /* A stage panel comes from a theme block, and a block cannot read another
     block's settings, so the section has no way to render the tab strip from
     the panels. The panels carry their own label instead and the strip is built
     here, ahead of the first one. Markup that already ships its own tabs, like
     the tabbed section, is left alone. */
  buildStageTabs() {
    const panels = Array.from(this.querySelectorAll('[data-stage-panel]'));
    if (!panels.length || this.querySelector('[role="tab"]')) return;

    const list = document.createElement('div');
    list.className = 'stages';
    list.setAttribute('role', 'tablist');
    const label = this.getAttribute('data-tablist-label');
    if (label) list.setAttribute('aria-label', label);

    panels.forEach((panel) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'stage-tab';
      btn.setAttribute('role', 'tab');
      btn.id = panel.id + '-tab';
      btn.setAttribute('aria-controls', panel.id);
      btn.setAttribute('aria-selected', String(!panel.hidden));

      const n = document.createElement('span');
      n.className = 'stage-tab__n';
      n.textContent = panel.dataset.stageNumber || '';
      const t = document.createElement('span');
      t.className = 'stage-tab__t';
      t.textContent = panel.dataset.stageLabel || '';
      if (n.textContent) btn.appendChild(n);
      if (t.textContent) btn.appendChild(t);

      panel.setAttribute('aria-labelledby', btn.id);
      list.appendChild(btn);
    });

    this.insertBefore(list, panels[0]);
  }

  select(tab, focus) {
    this.tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.setAttribute('tabindex', on ? '0' : '-1');
      // Panels are addressed by id so a panel may live outside this element.
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  }
}

/* --------------------------------------------------------------------------
   Reveal on scroll - <js-learn-reveal> observes its own .reveal descendants
   -------------------------------------------------------------------------- */
class JsLearnReveal extends HTMLElement {
  connectedCallback() {
    const els = this.querySelectorAll('.reveal');
    if (!els.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduced) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          this.io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    els.forEach((el) => this.io.observe(el));
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }
}

/* --------------------------------------------------------------------------
   Accordion - <js-learn-accordion> over .acc__btn / aria-expanded
   -------------------------------------------------------------------------- */
class JsLearnAccordion extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.acc__btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = open;
      });
    });
  }
}

/* --------------------------------------------------------------------------
   Select - <js-learn-select> stands in for the browser's own dropdown
   --------------------------------------------------------------------------
   The learn pages never show an OS dropdown: its list is drawn by the platform,
   ignores the design system and looks wrong inside the fit tool.

   The native <select> stays in the DOM as the data model, so whatever fills it
   with options, reads .value or listens for 'change' carries on working. This
   element hides it and renders a button plus an ARIA listbox in its place, and
   a MutationObserver keeps that list in step - the fit guide rebuilds its
   options on every chart switch.

   Keyboard behaviour follows the APG select-only combobox: focus stays on the
   button, aria-activedescendant points at the active option.
   -------------------------------------------------------------------------- */
let jsLearnSelectUid = 0;

class JsLearnSelect extends HTMLElement {
  connectedCallback() {
    this.native = this.querySelector('select');
    if (!this.native) return;
    if (!this.btn) this.build();

    this.onDocPointer = (e) => {
      if (!this.contains(e.target)) this.close(false);
    };
    this.onNativeChange = () => this.sync();
    this.native.addEventListener('change', this.onNativeChange);

    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.native, { childList: true, subtree: true, characterData: true });

    this.sync();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.native) this.native.removeEventListener('change', this.onNativeChange);
    // The theme editor moves a section by re-inserting it, so leave the element
    // closed rather than reconnecting with an orphaned list still on screen.
    this.close(false);
    if (this.onDocPointer) document.removeEventListener('pointerdown', this.onDocPointer, true);
  }

  /* ----------------------------------------------------------------- build */

  build() {
    const uid = this.native.id || 'js-learn-select-' + ++jsLearnSelectUid;
    this.native.id = uid;
    this.native.hidden = true;
    this.native.tabIndex = -1;

    this.btn = document.createElement('button');
    this.btn.type = 'button';
    this.btn.className = 'jselect__btn';
    this.btn.id = uid + '-btn';
    this.btn.setAttribute('role', 'combobox');
    this.btn.setAttribute('aria-haspopup', 'listbox');
    this.btn.setAttribute('aria-expanded', 'false');
    this.btn.setAttribute('aria-controls', uid + '-list');
    this.btn.innerHTML = '<span class="jselect__value"></span>';

    this.list = document.createElement('ul');
    this.list.className = 'jselect__list';
    this.list.id = uid + '-list';
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;

    // The field label addressed the select, which is no longer focusable.
    const field = this.closest('.field');
    this.label = field ? field.querySelector('label') : null;
    if (this.label) {
      if (!this.label.id) this.label.id = uid + '-label';
      this.label.removeAttribute('for');
      this.label.addEventListener('click', () => this.btn.focus());
      this.btn.setAttribute('aria-labelledby', this.label.id + ' ' + this.btn.id);
      this.list.setAttribute('aria-labelledby', this.label.id);
    }

    this.append(this.btn, this.list);

    this.btn.addEventListener('click', () => (this.isOpen ? this.close(true) : this.open()));
    this.btn.addEventListener('keydown', (e) => this.onKey(e));
    this.list.addEventListener('click', (e) => {
      const li = e.target.closest('[role="option"]');
      if (li) this.commit(this.options.indexOf(li));
    });
    this.list.addEventListener('mousemove', (e) => {
      const li = e.target.closest('[role="option"]');
      if (li) this.setActive(this.options.indexOf(li));
    });
  }

  /* ------------------------------------------------------------------ sync */

  sync() {
    if (!this.btn) return;
    const opts = Array.from(this.native.options);
    const selected = this.native.selectedIndex;

    this.list.innerHTML = '';
    this.options = opts.map((o, i) => {
      const li = document.createElement('li');
      li.className = 'jselect__opt';
      li.id = this.list.id + '-opt-' + i;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(i === selected));
      li.dataset.value = o.value;
      li.textContent = o.textContent;
      this.list.appendChild(li);
      return li;
    });

    const current = opts[selected];
    this.btn.querySelector('.jselect__value').textContent = current
      ? current.textContent
      : this.dataset.placeholder || '';
    this.btn.disabled = !opts.length;
    if (!opts.length) this.close(false);
    this.setActive(selected < 0 ? 0 : selected);
  }

  /* --------------------------------------------------------------- opening */

  open() {
    if (this.isOpen || !this.options || !this.options.length) return;
    this.isOpen = true;
    this.list.hidden = false;
    this.classList.add('is-open');
    this.btn.setAttribute('aria-expanded', 'true');
    this.setActive(this.native.selectedIndex < 0 ? 0 : this.native.selectedIndex);
    document.addEventListener('pointerdown', this.onDocPointer, true);
  }

  close(focus) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.list.hidden = true;
    this.classList.remove('is-open');
    this.btn.setAttribute('aria-expanded', 'false');
    this.btn.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    if (focus) this.btn.focus();
  }

  setActive(i) {
    if (!this.options || !this.options.length) return;
    this.activeIndex = Math.max(0, Math.min(i, this.options.length - 1));
    this.options.forEach((li, n) => li.classList.toggle('is-active', n === this.activeIndex));
    const li = this.options[this.activeIndex];
    if (this.isOpen) {
      this.btn.setAttribute('aria-activedescendant', li.id);
      li.scrollIntoView({ block: 'nearest' });
    }
  }

  commit(i) {
    const li = this.options && this.options[i];
    if (!li) return;
    if (li.dataset.value !== this.native.value) {
      this.native.value = li.dataset.value;
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.sync();
    this.close(true);
  }

  /* -------------------------------------------------------------- keyboard */

  onKey(e) {
    const printable = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;

    if (!this.isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.open();
      } else if (printable) {
        e.preventDefault();
        this.search(e.key);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.setActive(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setActive(this.activeIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        e.preventDefault();
        this.setActive(this.options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.commit(this.activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        this.close(true);
        break;
      case 'Tab':
        this.close(false);
        break;
      default:
        if (printable) {
          e.preventDefault();
          this.search(e.key);
        }
    }
  }

  /* Type a letter to jump to a label, as a native dropdown does. */
  search(char) {
    if (!this.options || !this.options.length) return;
    clearTimeout(this.searchTimer);
    this.query = (this.query || '') + char.toLowerCase();
    this.searchTimer = setTimeout(() => {
      this.query = '';
    }, 600);

    const i = this.options.findIndex((li) =>
      li.textContent.trim().toLowerCase().startsWith(this.query)
    );
    if (i === -1) return;
    if (this.isOpen) this.setActive(i);
    else this.commit(i);
  }
}

/* --------------------------------------------------------------------------
   Scroll spy - <js-learn-spy> marks the sub-nav link whose section is in view
   --------------------------------------------------------------------------
   The sections live outside this element, so the lookups are document wide.
   The rootMargin keeps a band across the middle of the viewport, which stops
   two sections claiming the highlight at once.
   -------------------------------------------------------------------------- */
class JsLearnSpy extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('a[href^="#"]'));
    if (!this.links.length || !('IntersectionObserver' in window)) return;

    this.map = new Map();
    const targets = this.links
      .map((a) => {
        const el = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
        if (el) this.map.set(el.id, a);
        return el;
      })
      .filter(Boolean);
    if (!targets.length) return;

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const active = this.map.get(entry.target.id);
          if (!active) return;
          this.links.forEach((a) => a.classList.remove('is-active'));
          active.classList.add('is-active');
          this.keepInView(active);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );

    targets.forEach((t) => this.io.observe(t));
  }

  /* On narrow screens the link row scrolls, so bring the active one along. */
  keepInView(active) {
    const scroller = this.querySelector('.subnav__scroll');
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    scroller.scrollTo({
      left: active.offsetLeft - scroller.clientWidth / 2 + active.offsetWidth / 2,
      behavior: 'smooth'
    });
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }
}

/* --------------------------------------------------------------------------
   Lightbox - <js-learn-lightbox> opens its own links at full size
   --------------------------------------------------------------------------
   A fit chart is unreadable at tile size, and the live page answers that by
   opening the chart in a lightbox rather than navigating away from the page
   the reader is on. This is that behaviour.

   Contract: any descendant <a data-lightbox href="<full size image>"> becomes
   a trigger, and the data-lightbox value is the caption. The href stays a real
   link to the image, so with JS off or still loading the chart is one click
   away rather than nowhere.

   The overlay is a native <dialog>, so the focus trap, the backdrop and the
   Escape key come from the platform instead of from us. Several triggers under
   one element form a set the arrows step through.
   -------------------------------------------------------------------------- */
class JsLearnLightbox extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('a[data-lightbox]'));
    if (!this.items.length || this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.items.forEach((item, i) => {
      item.addEventListener('click', (event) => {
        /* Leave the modified clicks alone: someone asking for a new tab wants
           the image itself, which is what the href already points at. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        event.preventDefault();
        this.open(i);
      });
    });
  }

  disconnectedCallback() {
    if (!this.dialog) return;
    this.dialog.remove();
    this.dialog = null;
  }

  build() {
    if (this.dialog) return;

    /* The dialog renders in the top layer, outside this section's .js-learn
       wrapper, so it has to carry the scope class itself or none of the
       stylesheet reaches it. */
    const dialog = document.createElement('dialog');
    dialog.className = 'js-learn lb';
    dialog.innerHTML = [
      '<div class="lb__frame">',
      '<div class="lb__stage"><img class="lb__img" alt=""></div>',
      '<p class="lb__cap"></p>',
      '<button class="lb__zoom" type="button" aria-label="Zoom in" aria-pressed="false">',
      '<svg class="lb__zoom-in" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5"/><path d="M12.5 12.5 17 17M8.5 6v5M6 8.5h5"/></svg>',
      '<svg class="lb__zoom-out" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5"/><path d="M12.5 12.5 17 17M6 8.5h5"/></svg>',
      '</button>',
      '<button class="lb__close" type="button" aria-label="Close">&times;</button>',
      '<button class="lb__nav lb__nav--prev" type="button" aria-label="Previous image">&#8249;</button>',
      '<button class="lb__nav lb__nav--next" type="button" aria-label="Next image">&#8250;</button>',
      '</div>'
    ].join('');

    this.dialog = dialog;
    this.image = dialog.querySelector('.lb__img');
    this.caption = dialog.querySelector('.lb__cap');
    this.stage = dialog.querySelector('.lb__stage');
    this.zoomButton = dialog.querySelector('.lb__zoom');

    /* A tap on the chart zooms in at the point tapped, and a second tap zooms
       back out. The button does the same from the centre, for anyone who
       would not think to tap the picture. */
    this.image.addEventListener('click', (event) => this.toggleZoom(event));
    this.zoomButton.addEventListener('click', () => this.toggleZoom());

    dialog.querySelector('.lb__close').addEventListener('click', () => dialog.close());
    dialog.querySelector('.lb__nav--prev').addEventListener('click', () => this.step(-1));
    dialog.querySelector('.lb__nav--next').addEventListener('click', () => this.step(1));

    /* A click that lands on the dialog and not on the frame inside it is a
       click on the dimmed area, which everyone expects to dismiss. */
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    });

    dialog.addEventListener('close', () => {
      this.setZoom(false);
      this.allowPinch(false);
      document.documentElement.style.overflow = this.heldOverflow || '';
      const trigger = this.items[this.index];
      if (trigger) trigger.focus();
    });

    document.body.appendChild(dialog);
  }

  open(index) {
    this.build();
    this.dialog.classList.toggle('lb--single', this.items.length < 2);
    this.show(index);
    /* showModal blocks interaction but not scrolling, and a page that drifts
       behind the overlay loses the reader's place. */
    this.heldOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.allowPinch(true);
    this.dialog.showModal();
  }

  /* The theme's viewport meta sets maximum-scale=1, which switches off pinch
     zoom across the site. While a chart is open the cap is lifted so a reader
     can pinch the chart, and it goes back as soon as the lightbox closes. */
  allowPinch(on) {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    if (on) {
      if (this.heldViewport == null) this.heldViewport = meta.getAttribute('content') || '';
      const relaxed = this.heldViewport
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part && !/^(maximum-scale|user-scalable)\s*=/i.test(part))
        .concat('maximum-scale=5', 'user-scalable=yes')
        .join(', ');
      meta.setAttribute('content', relaxed);
    } else if (this.heldViewport != null) {
      meta.setAttribute('content', this.heldViewport);
      this.heldViewport = null;
    }
  }

  toggleZoom(event) {
    this.setZoom(!this.zoomed, event);
  }

  setZoom(on, event) {
    if (!this.dialog || !this.image) return;
    if (!on) {
      this.zoomed = false;
      this.dialog.classList.remove('lb--zoomed');
      this.image.style.width = '';
      this.stage.scrollTo(0, 0);
      this.zoomButton.setAttribute('aria-pressed', 'false');
      this.zoomButton.setAttribute('aria-label', 'Zoom in');
      return;
    }

    /* Work out where the reader tapped, as a fraction of the fitted image,
       before the layout changes underneath it. */
    const box = this.image.getBoundingClientRect();
    const fx = event && event.clientX != null && box.width ? (event.clientX - box.left) / box.width : 0.5;
    const fy = event && event.clientY != null && box.height ? (event.clientY - box.top) / box.height : 0.5;

    /* Two and a half times the fitted size reads comfortably on a phone; never
       past the image's own pixels, and always wider than the screen. */
    const natural = this.image.naturalWidth || box.width * 2.5;
    const width = Math.max(Math.min(natural, box.width * 2.5), window.innerWidth * 1.5);

    this.zoomed = true;
    this.dialog.classList.add('lb--zoomed');
    this.image.style.width = Math.round(width) + 'px';
    this.zoomButton.setAttribute('aria-pressed', 'true');
    this.zoomButton.setAttribute('aria-label', 'Zoom out');

    requestAnimationFrame(() => {
      const w = this.image.offsetWidth;
      const h = this.image.offsetHeight;
      this.stage.scrollTo(
        Math.max(0, fx * w - this.stage.clientWidth / 2),
        Math.max(0, fy * h - this.stage.clientHeight / 2)
      );
    });
  }

  show(index) {
    this.setZoom(false);
    this.index = (index + this.items.length) % this.items.length;
    const item = this.items[this.index];
    const thumb = item.querySelector('img');
    const caption = item.dataset.lightbox;

    this.image.src = item.getAttribute('href');
    this.image.alt = thumb ? thumb.alt : caption || '';
    this.caption.textContent = caption || '';
    this.caption.hidden = !caption;
  }

  step(by) {
    if (this.items.length > 1) this.show(this.index + by);
  }
}

/* --------------------------------------------------------------------------
   Video - <js-learn-video> is a facade over a YouTube embed
   --------------------------------------------------------------------------
   The thumbnail and a play button stand in until someone clicks, so a page with
   a video on it does not load YouTube's player, or set its cookies, for the
   majority of visitors who never press play.
   -------------------------------------------------------------------------- */
class JsLearnVideo extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.yt;
    if (!id || this.dataset.ready) return;
    this.dataset.ready = 'true';

    const start = this.dataset.start || '0';
    const label = this.dataset.label || 'Watch the video';

    this.style.backgroundImage = 'url(https://i.ytimg.com/vi/' + id + '/hqdefault.jpg)';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'yt__btn';
    btn.innerHTML = '<span class="yt__play" aria-hidden="true"></span><span class="yt__label"></span>';
    btn.querySelector('.yt__label').textContent = label;
    this.replaceChildren(btn);

    btn.addEventListener('click', () => {
      const frame = document.createElement('iframe');
      frame.src = 'https://www.youtube.com/embed/' + id + '?start=' + start + '&autoplay=1&rel=0';
      frame.title = label;
      frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
      frame.allowFullscreen = true;
      this.replaceChildren(frame);
    });
  }
}

/* --------------------------------------------------------------------------
   Read more - <js-learn-more> collapses the tail of a card body
   --------------------------------------------------------------------------
   A product card whose spec list runs seven bullets long turns a grid of cards
   into a wall of bullets, and the row is only ever as short as its longest
   card. The section splits the body at its first list, leaves the lead
   paragraph showing and hands the rest to this element.

   Nothing is removed: the panel is rendered open, and only becomes collapsible
   once this runs. Without JavaScript the card reads exactly as it did before,
   full list and no button. The toggle button is built here for the same
   reason - a button that cannot do anything should not be in the markup.

   The height animation is grid-template-rows 0fr to 1fr, so it works off the
   panel's own content height and needs no measuring.

   Each toggle opens its own card and nothing else. Lining the cards up is
   js-learn-even-grid's job, below, and deliberately not a grid stretch.
   -------------------------------------------------------------------------- */
let jsLearnMoreUid = 0;

class JsLearnMore extends HTMLElement {
  connectedCallback() {
    const panel = this.querySelector('.more__panel');
    if (!panel || this.btn) return;

    const uid = ++jsLearnMoreUid;
    this.labelMore = this.dataset.labelMore || 'Read more';
    this.labelLess = this.dataset.labelLess || 'Read less';

    if (!panel.id) panel.id = 'more-panel-' + uid;
    panel.setAttribute('role', 'region');

    this.btn = document.createElement('button');
    this.btn.type = 'button';
    this.btn.className = 'more__btn';
    this.btn.id = 'more-btn-' + uid;
    this.btn.setAttribute('aria-expanded', 'false');
    this.btn.setAttribute('aria-controls', panel.id);
    this.btn.innerHTML = '<span class="more__label"></span><span class="more__chev" aria-hidden="true"></span>';
    this.label = this.btn.querySelector('.more__label');
    this.label.textContent = this.labelMore;
    panel.setAttribute('aria-labelledby', this.btn.id);
    this.append(this.btn);

    /* more--init suppresses the transition for the first frame, so the panel
       does not play a collapse animation on page load. */
    this.classList.add('more--ready', 'more--init');
    requestAnimationFrame(() => this.classList.remove('more--init'));

    this.btn.addEventListener('click', () => {
      this.setOpen(!this.classList.contains('is-open'));
    });
  }

  setOpen(open) {
    this.classList.toggle('is-open', open);
    if (this.btn) {
      this.btn.setAttribute('aria-expanded', String(open));
      this.label.textContent = open ? this.labelLess : this.labelMore;
    }
  }
}

/* --------------------------------------------------------------------------
   Even grid - <js-learn-even-grid> puts the cards in a row to one height
   --------------------------------------------------------------------------
   A grid stretch cannot do this. A stretched card fills its row and a row is
   as tall as its tallest card, so opening one card makes the row taller and
   every neighbour grows with it: the whole row appears to open. Dropping the
   stretch the moment a card opens is worse, because the stretch was holding up
   to 160px of padding on these cards - releasing it made all seven jump down
   at once, and the card being opened ended up shorter than it started.

   So the row height is a min-height on each card rather than a stretch: the
   tallest card in the row while all of them are shut. An open card grows past
   its own minimum, its neighbours keep theirs, and nothing else moves.

   Re-measured on a width change only. Observing height as well would feed back
   on itself, since setting the minimums changes the height being observed.
   -------------------------------------------------------------------------- */
class JsLearnEvenGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.grid');
    if (!this.grid) return;

    this.measure = this.measure.bind(this);

    this.ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (w === this.lastWidth) return;
      this.lastWidth = w;
      this.measure();
    });
    this.ro.observe(this);

    /* A card's height is not final until its photo and the webfonts are in. */
    this.addEventListener('load', this.measure, true);
    if (document.fonts) document.fonts.ready.then(this.measure).catch(() => {});

    /* A card closing returns it to its own content height, which is the height
       the minimums were measured from, so nothing needs re-measuring. A width
       change while a card is open does, and that is what this covers. */
    this.addEventListener('transitionend', (e) => {
      if (e.target.classList.contains('more__panel') && this.pending) this.measure();
    });

    this.measure();
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
  }

  measure() {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      const cards = Array.from(this.grid.children).filter((el) => el.classList.contains('card'));
      if (!cards.length) return;

      /* An open card is taller than its shut height and would set a row
         minimum no other card should have to match, so wait until it is shut.
         The minimums already in place are left alone: clearing them here is
         what made opening every card drop the whole grid to ragged heights,
         with a card ending up shorter open than it had been shut. A stale
         minimum from the previous width is the far smaller wrong. */
      if (this.grid.querySelector('js-learn-more.is-open')) {
        this.pending = true;
        return;
      }
      this.pending = false;

      cards.forEach((card) => { card.style.minHeight = ''; });

      /* offsetTop groups the cards by row, so a grid that has wrapped to two
         columns or one is handled without knowing the column count. */
      const rows = new Map();
      const tops = cards.map((card) => {
        const top = Math.round(card.offsetTop);
        const h = card.getBoundingClientRect().height;
        rows.set(top, Math.max(rows.get(top) || 0, h));
        return top;
      });
      cards.forEach((card, i) => {
        card.style.minHeight = Math.ceil(rows.get(tops[i])) + 'px';
      });
    });
  }
}

for (const [name, klass] of [
  ['js-learn-tabs', JsLearnTabs],
  ['js-learn-more', JsLearnMore],
  ['js-learn-even-grid', JsLearnEvenGrid],
  ['js-learn-reveal', JsLearnReveal],
  ['js-learn-accordion', JsLearnAccordion],
  ['js-learn-select', JsLearnSelect],
  ['js-learn-spy', JsLearnSpy],
  ['js-learn-video', JsLearnVideo],
  ['js-learn-lightbox', JsLearnLightbox]
]) {
  if (!customElements.get(name)) customElements.define(name, klass);
}
